// src/lib/travelHealth/scheduling/projectSchedule.ts
//
// Pure schedule projection engine. No React, no Zustand, no store imports.
// All schedule/availability/member data is supplied by the caller; this
// module contains no clinical values.

import type {
  AppointmentSlot,
  ProjectedDose,
  ProjectionResult,
  ScheduleFeasibility,
  ScheduledProduct,
} from "./types";
import type { PgdId } from "../pgds/pgdIds";

type DayName =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

interface DayAvailability {
  enabled: boolean;
  timeBlocks: Array<{ from: string; to: string }>;
}

export interface SchedulingMember {
  id: string;
  isClinician: boolean;
  active: boolean;
  servicesProvided?: string[];
  availability?: Record<string, DayAvailability>;
}

interface AcceleratedAlternative {
  pgdId: string;
  scheduleId: string;
  scheduleLabel: string;
  doses: Array<{
    doseNumber: number;
    label: string;
    intervalFromDose1Days?: number;
  }>;
}

interface ProjectTravelHealthScheduleInput {
  // ISO date string — starting point for dose 1 of every product (typically today)
  startDate: string;
  // ISO date string — patient's departure date
  departureDate: string;
  // Products with their chosen schedules
  products: ScheduledProduct[];
  // Site availability — shape matches Location room availability from locationsData.ts
  siteAvailability: Record<string, DayAvailability>;
  // Active clinician members for pharmacist filtering
  members: SchedulingMember[];
  // Optional: for feasibility comparison only.
  acceleratedAlternatives?: AcceleratedAlternative[];
}

const DAY_ORDER: DayName[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const DAY_LABELS: Record<DayName, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const NEXT_OPEN_DAY_CAP = 14;

// HELPER 6 — returns just the YYYY-MM-DD portion of any ISO string
function toISODateOnly(isoDate: string): string {
  return isoDate.split("T")[0];
}

// HELPER 1 — adds N calendar days to an ISO date string, no timezone conversion
function addDays(isoDate: string, days: number): string {
  const datePart = toISODateOnly(isoDate);
  const [year, month, day] = datePart.split("-").map(Number);
  const utcMs = Date.UTC(year, month - 1, day) + days * 24 * 60 * 60 * 1000;
  const result = new Date(utcMs);
  const resultYear = result.getUTCFullYear();
  const resultMonth = String(result.getUTCMonth() + 1).padStart(2, "0");
  const resultDay = String(result.getUTCDate()).padStart(2, "0");
  return `${resultYear}-${resultMonth}-${resultDay}`;
}

// HELPER 2 — returns the lowercase day name for an ISO date
function getDayName(isoDate: string): DayName {
  const datePart = toISODateOnly(isoDate);
  const [year, month, day] = datePart.split("-").map(Number);
  const utcMs = Date.UTC(year, month - 1, day);
  return DAY_ORDER[new Date(utcMs).getUTCDay()];
}

// HELPER 3 — returns true if the site is open on the given date
function isSiteOpen(
  isoDate: string,
  siteAvailability: Record<string, DayAvailability>,
): boolean {
  const day = getDayName(isoDate);
  const dayAvailability = siteAvailability[day];
  return (
    dayAvailability !== undefined &&
    dayAvailability.enabled === true &&
    dayAvailability.timeBlocks.length > 0
  );
}

// HELPER 4 — advances to the next open day, capped at 14 iterations
function nextOpenDay(
  isoDate: string,
  siteAvailability: Record<string, DayAvailability>,
): string {
  let candidate = toISODateOnly(isoDate);
  if (isSiteOpen(candidate, siteAvailability)) {
    return candidate;
  }
  for (let i = 0; i < NEXT_OPEN_DAY_CAP; i++) {
    candidate = addDays(candidate, 1);
    if (isSiteOpen(candidate, siteAvailability)) {
      return candidate;
    }
  }
  console.warn(
    `nextOpenDay: no open day found within ${NEXT_OPEN_DAY_CAP} days of ${isoDate}; returning original date`,
  );
  return toISODateOnly(isoDate);
}

// HELPER 5 — returns ids of qualified, available pharmacists on a given date
export function getQualifiedPharmacists(
  isoDate: string,
  members: SchedulingMember[],
): string[] {
  const day = getDayName(isoDate);
  return members
    .filter((member) => {
      if (!member.isClinician || !member.active) {
        return false;
      }
      if (!member.servicesProvided?.includes("Travel Health")) {
        return false;
      }
      if (!member.availability) {
        return true;
      }
      const dayAvailability = member.availability[day];
      return (
        dayAvailability !== undefined &&
        dayAvailability.enabled === true &&
        dayAvailability.timeBlocks.length > 0
      );
    })
    .map((member) => member.id);
}

// Returns the positive number of calendar days between two ISO date strings
function daysBetween(earlier: string, later: string): number {
  const earlierPart = toISODateOnly(earlier);
  const laterPart = toISODateOnly(later);
  const [earlierYear, earlierMonth, earlierDay] = earlierPart
    .split("-")
    .map(Number);
  const [laterYear, laterMonth, laterDay] = laterPart.split("-").map(Number);
  const earlierMs = Date.UTC(earlierYear, earlierMonth - 1, earlierDay);
  const laterMs = Date.UTC(laterYear, laterMonth - 1, laterDay);
  return Math.abs(Math.round((laterMs - earlierMs) / (24 * 60 * 60 * 1000)));
}

interface ProjectDosesResult {
  projectedDoses: ProjectedDose[];
  adjustmentLog: ProjectionResult["adjustmentLog"];
}

function projectDoses(
  product: Pick<
    ScheduledProduct,
    "pgdId" | "productName" | "doses" | "serviceType" | "blackTriangle"
  >,
  startDate: string,
  departureDate: string,
  siteAvailability: Record<string, DayAvailability>,
): ProjectDosesResult {
  const projectedDoses: ProjectedDose[] = [];
  const adjustmentLog: ProjectionResult["adjustmentLog"] = [];
  const normalizedStartDate = toISODateOnly(startDate);
  const normalizedDepartureDate = toISODateOnly(departureDate);

  for (const dose of product.doses) {
    const rawDate =
      dose.doseNumber === 1
        ? normalizedStartDate
        : addDays(normalizedStartDate, dose.intervalFromDose1Days ?? 0);
    const adjustedDate = nextOpenDay(rawDate, siteAvailability);
    const wasAdjusted = adjustedDate !== rawDate;
    const isAfterDeparture = adjustedDate > normalizedDepartureDate;

    projectedDoses.push({
      pgdId: product.pgdId,
      productName: product.productName,
      doseNumber: dose.doseNumber,
      doseLabel: dose.label,
      rawDate,
      adjustedDate,
      wasAdjusted,
      isAfterDeparture,
      serviceType: product.serviceType,
      blackTriangle: product.blackTriangle,
    });

    if (wasAdjusted) {
      adjustmentLog.push({
        pgdId: product.pgdId,
        doseNumber: dose.doseNumber,
        originalDate: rawDate,
        adjustedDate,
        reason: "Site closed on original date",
      });
    }
  }

  return { projectedDoses, adjustmentLog };
}

export function projectTravelHealthSchedule(
  input: ProjectTravelHealthScheduleInput,
): ProjectionResult {
  const normalizedDepartureDate = toISODateOnly(input.departureDate);

  // STEP 1 — Project raw dose dates for every product
  const allProjectedDoses: ProjectedDose[] = [];
  const adjustmentLog: ProjectionResult["adjustmentLog"] = [];

  for (const product of input.products) {
    const { projectedDoses, adjustmentLog: productAdjustmentLog } =
      projectDoses(
        product,
        input.startDate,
        input.departureDate,
        input.siteAvailability,
      );
    allProjectedDoses.push(...projectedDoses);
    adjustmentLog.push(...productAdjustmentLog);
  }

  // STEP 2 — Group projected doses by adjustedDate
  const dosesByDate = new Map<string, ProjectedDose[]>();
  for (const dose of allProjectedDoses) {
    const existing = dosesByDate.get(dose.adjustedDate);
    if (existing) {
      existing.push(dose);
    } else {
      dosesByDate.set(dose.adjustedDate, [dose]);
    }
  }

  // STEP 3 — Build AppointmentSlots
  const appointmentSlots: AppointmentSlot[] = Array.from(
    dosesByDate.entries(),
  ).map(([date, doses]) => {
    const dayName = getDayName(date);
    const availablePharmacistIds = getQualifiedPharmacists(
      date,
      input.members,
    );
    return {
      date,
      dayOfWeek: DAY_LABELS[dayName],
      doses,
      availablePharmacistIds,
      noQualifiedPharmacistWarning: availablePharmacistIds.length === 0,
    };
  });
  appointmentSlots.sort((a, b) => a.date.localeCompare(b.date));

  // STEP 4 — Build feasibility per product
  const feasibility: ScheduleFeasibility[] = input.products.map((product) => {
    const projected = allProjectedDoses.filter(
      (dose) =>
        dose.pgdId === product.pgdId &&
        dose.productName === product.productName,
    );
    const lastDose = projected.reduce((latest, dose) =>
      dose.doseNumber > latest.doseNumber ? dose : latest,
    );
    const lastDoseDate = lastDose.adjustedDate;
    const exceedsDeparture = lastDoseDate > normalizedDepartureDate;
    const daysAfterDeparture = exceedsDeparture
      ? daysBetween(normalizedDepartureDate, lastDoseDate)
      : 0;

    const acceleratedAlternative = input.acceleratedAlternatives?.find(
      (alternative) => alternative.pgdId === (product.pgdId as PgdId),
    );

    let acceleratedLastDoseDate: string | undefined;
    let acceleratedFitsDeparture: boolean | undefined;

    if (acceleratedAlternative) {
      const { projectedDoses: acceleratedProjectedDoses } = projectDoses(
        {
          pgdId: acceleratedAlternative.pgdId,
          productName: product.productName,
          doses: acceleratedAlternative.doses,
          serviceType: product.serviceType,
          blackTriangle: product.blackTriangle,
        },
        input.startDate,
        input.departureDate,
        input.siteAvailability,
      );
      const acceleratedLastDose = acceleratedProjectedDoses.reduce(
        (latest, dose) => (dose.doseNumber > latest.doseNumber ? dose : latest),
      );
      acceleratedLastDoseDate = acceleratedLastDose.adjustedDate;
      acceleratedFitsDeparture =
        acceleratedLastDoseDate <= normalizedDepartureDate;
    }

    return {
      pgdId: product.pgdId,
      productName: product.productName,
      selectedScheduleLabel: product.selectedScheduleLabel,
      lastDoseDate,
      exceedsDeparture,
      daysAfterDeparture,
      hasAcceleratedAlternative: acceleratedAlternative !== undefined,
      acceleratedScheduleId: acceleratedAlternative?.scheduleId,
      acceleratedScheduleLabel: acceleratedAlternative?.scheduleLabel,
      acceleratedLastDoseDate,
      acceleratedFitsDeparture,
    };
  });

  // STEP 5 — Build noFitWarnings
  const noFitWarnings = feasibility
    .filter(
      (entry) =>
        entry.exceedsDeparture &&
        (!entry.hasAcceleratedAlternative ||
          entry.acceleratedFitsDeparture === false),
    )
    .map((entry) => entry.pgdId);

  // STEP 6 — Return ProjectionResult
  return {
    appointmentSlots,
    feasibility,
    noFitWarnings,
    adjustmentLog,
  };
}
